/**
 * Street address data type
 */
export class Address {
  /**
   * Street address or PO Box
   */
  street1: string;

  /**
   * Apartment, unit, suite number, etc.
   */
  street2: string;

  /**
   * City or town
   */
  city: string;

  /**
   * State (U.S.), province (Canada), federal district (Mexico), county (U.K.), etc.
   */
  stateProvince: string;

  /**
   * Zip code (U.S.), postal code (Canada, Mexico), postcode (U.K.), etc.
   */
  postalCode: string;

  /**
   * Country
   */
  country: string;

  /**
   * Returns an instance of the Address class if there is sufficient information to populate an address, or null
   * otherwise.
   */
  static parse(street1, street2, city, stateProvince, postalCode, country): Address {
    if (street1 || street2 || city || stateProvince || postalCode || country) {
      return Object.assign(new Address(), {
        street1,
        street2,
        city,
        stateProvince,
        postalCode,
        country
      });
    }
    return null;
  }

  /**
   * Formats the full address as a string
   * @param [oneline] {boolean} If true, returns the address as a single line (defaults to false)
   * @return {string}
   */
  formatAddress(oneline = false): string {
    const street = this.formatStreet(oneline);
    const cityStateZip = this.formatCityStateZip();
    return street || cityStateZip
      ? [street, cityStateZip].filter(v => v).join(oneline ? ', ' : '\n')
      : null;
  }

  /**
   * Formats the street portion of the address as a string
   * @param [oneline] {boolean} If true, returns the address as a single line (defaults to false)
   * @return {string}
   */
  formatStreet(oneline = false): string {
    const street1 = (this.street1 || '').trim();
    const street2 = (this.street2 || '').trim();
    return street1 || street2
      ? [street1, street2].filter(v => v).join(oneline ? ', ' : '\n')
      : null;
  }

  /**
   * Formats the city and state portion of the address as a string
   * @return {string}
   */
  formatCityState(): string {
    const city = (this.city || '').trim();
    const state = (this.stateProvince || '').trim();
    if (city && state) {
      return `${city}, ${state}`;
    }
    if (state) {
      return state;
    }
    return null;
  }

  /**
   * Formats the postal code portion of the address
   * @return {string}
   */
  formatPostalCode(): string {
    const zip = (this.postalCode || '').trim();
    if (!zip) {
      return null;
    }
    return zip.length === 9
      ? zip.substr(0, 5) + '-' + zip.substr(5)
      : zip;
  }

  /**
   * Formats the city, state, and postal code portions of the address
   * @return {string}
   */
  formatCityStateZip(): string {
    const cityState = this.formatCityState();
    const postalCode = this.formatPostalCode();
    if (cityState && postalCode) {
      return `${cityState} ${postalCode}`;
    }
    if (cityState) {
      return cityState;
    }
    if (postalCode) {
      return postalCode;
    }
    return null;
  }
}
